package diana.soleil.design.iraniancanadienbazzar.auth

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.SignInScreen
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.SignInStatus
import org.koin.compose.viewmodel.koinViewModel

/**
 * Stateful host for [SignInScreen]: owns [AuthViewModel], runs the email/password sign-in & sign-up
 * and password-reset flows, and maps the coarse [AppError] onto the screen's [SignInStatus] (keeping
 * auth copy in the onboarding module). When the session becomes non-null the user is signed in and we
 * navigate on via [onAuthenticated]; guest continues without authenticating.
 */
@Composable
fun SignInRoute(
    onAuthenticated: () -> Unit,
    onContinueAsGuest: () -> Unit,
    onNavigateToSignUp: () -> Unit,
    onBack: (() -> Unit)? = null,
    viewModel: AuthViewModel = koinViewModel(),
) {
    val ui by viewModel.ui.collectAsStateWithLifecycle()
    val session by viewModel.session.collectAsStateWithLifecycle()

    // A real account signs in → leave the auth flow. Anonymous sessions don't count: the guest path
    // navigates itself (below) and the fire-and-forget anonymous sign-in must not re-trigger nav.
    LaunchedEffect(session) {
        if (session?.isAnonymous == false) onAuthenticated()
    }

    SignInScreen(
        onSignIn = viewModel::signIn,
        onSignUp = onNavigateToSignUp,
        onGoogle = if (viewModel.googleSignInAvailable) viewModel::signInWithGoogle else null,
        onApple = if (viewModel.appleSignInAvailable) viewModel::signInWithApple else null,
        onContinueAsGuest = {
            viewModel.continueAsGuest()
            onContinueAsGuest()
        },
        onForgotPassword = viewModel::sendPasswordReset,
        isLoading = ui.loading,
        status = ui.toStatus(),
        // Clear a stale error/reset banner as the user edits a field to retry.
        onFieldEdited = viewModel::consumeMessages,
        onBack = onBack,
    )
}

private fun AuthUiState.toStatus(): SignInStatus? = when {
    resetEmailSent -> SignInStatus.ResetSent
    invalidEmail -> SignInStatus.InvalidEmail
    error == null -> null
    error is AppError.Conflict -> SignInStatus.EmailInUse
    error is AppError.InvalidCredentials -> SignInStatus.InvalidCredentials
    error is AppError.Network -> SignInStatus.Network
    else -> SignInStatus.Generic
}
